import { useMe } from "@auth/useMe"
import {
  queryOptions,
  useMutation,
  type UseMutationResult,
} from "@tanstack/react-query"
import { request } from "@utils/request"

import type { FeatureFlags } from "./featureFlags.query"
import type { Modules, SolutionSettings } from "./solutionSettings"
import { solutionSettingsQuery } from "./solutionSettings.query"

export type SolutionListResponse = {
  solutions: {
    solutionUid: string
    name: string
    modules: Modules
    customer: { name: string }
  }[]
}

export const superpowersSolutionListQuery = queryOptions({
  queryKey: ["solutionList"] as const,
  queryFn: () =>
    request.get<SolutionListResponse>("/api/systemadmin/v1/solutions"),
})

export const superpowersSolutionSettingsQuery = (solutionUid: string) =>
  queryOptions({
    queryKey: ["solutionSettings", solutionUid] as const,
    queryFn: () =>
      request.get<SolutionSettings>(
        `/api/systemadmin/v1/solutions/${solutionUid}`,
      ),
  })

export const useUpdateSuperpowersSolutionSettings = (
  solutionUid: string,
): UseMutationResult<
  Partial<SolutionSettings>,
  Error,
  Partial<SolutionSettings>,
  { previous: SolutionSettings | undefined }
> => {
  const { solutionUid: ownSolutionUid } = useMe()
  const isOwnSolution = ownSolutionUid === solutionUid

  return useMutation({
    mutationFn: (data: Partial<SolutionSettings>) =>
      request.patch<Partial<SolutionSettings>>(
        `/api/systemadmin/v1/solutions/${solutionUid}`,
        { json: data },
      ),
    onMutate: (data, { client }) => {
      const previous = client.getQueryData<SolutionSettings>(
        superpowersSolutionSettingsQuery(solutionUid).queryKey,
      )
      client.setQueryData<SolutionSettings>(
        superpowersSolutionSettingsQuery(solutionUid).queryKey,
        (oldData) => (oldData ? { ...oldData, ...data } : oldData),
      )
      return { previous }
    },
    onError: (_error, _data, onMutateResult, { client }) => {
      client.setQueryData(
        superpowersSolutionSettingsQuery(solutionUid).queryKey,
        onMutateResult?.previous,
      )
    },
    onSuccess: (_data, _variables, _onMutateResult, { client }) => {
      if (isOwnSolution) {
        client.removeQueries({
          queryKey: solutionSettingsQuery.queryKey,
          exact: true,
        })
      }
    },
  })
}

export const superpowersFeatureFlagsQuery = (solutionUid: string) =>
  queryOptions({
    queryKey: ["featureFlags", solutionUid] as const,
    queryFn: () =>
      request.get<FeatureFlags>(
        `/api/systemadmin/v1/solutions/${solutionUid}/flags`,
      ),
  })

export const useUpdateFeatureFlags = (solutionUid: string) =>
  useMutation({
    mutationFn: (flags: Record<string, boolean>) =>
      request.put<Record<string, boolean>>(
        `/api/systemadmin/v1/solutions/${solutionUid}/flags`,
        { json: flags },
      ),
    onMutate: (flags, { client }) => {
      const previous = client.getQueryData<FeatureFlags>(
        superpowersFeatureFlagsQuery(solutionUid).queryKey,
      )
      client.setQueryData<FeatureFlags>(
        superpowersFeatureFlagsQuery(solutionUid).queryKey,
        (oldData) => (oldData ? { ...oldData, flags } : oldData),
      )
      return { previous }
    },
    onError: (_error, _flags, onMutateResult, { client }) => {
      client.setQueryData(
        superpowersFeatureFlagsQuery(solutionUid).queryKey,
        onMutateResult?.previous,
      )
    },
  })
